import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import {
  Backdrop,
  ModalContainer,
  CloseButton,
  ModalHeader,
  ModalTitle,
  ModalFooter,
  SecondaryButton,
} from './StyledComponents';

// Close icon SVG
const CloseIcon = () => (
  <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z" />
  </svg>
);

// Warning icon
const WarningIcon = () => (
  <svg
    width="48"
    height="48"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  </svg>
);

// Message body
const ModalBody = styled.div`
  padding: 0 32px 24px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 0 24px 20px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  color: ${(props) => props.theme.colors?.error || '#ED4337'};
`;

const Message = styled.p`
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondary || '#94A3B8'
      : '#64748b'};
`;

// Delete button (red variant)
const DeleteButton = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  color: white;
  background: ${(props) => props.theme.colors?.error || '#ED4337'};
  min-width: 120px;

  &:hover:not(:disabled) {
    background: #d63030;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(237, 67, 55, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:focus {
    outline: 2px solid ${(props) => props.theme.colors?.error || '#ED4337'};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CenteredFooter = styled(ModalFooter)`
  justify-content: center;
`;

/**
 * DeleteConfirmModal - A confirmation modal for delete actions
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {Function} onClose - Callback when modal is closed
 * @param {string} title - Modal title
 * @param {string} message - Confirmation message
 * @param {string} confirmText - Text for confirm/delete button
 * @param {string} cancelText - Text for cancel button
 * @param {Function} onConfirm - Confirm/delete handler
 * @param {boolean} isDeleting - Loading state for delete button
 */
const DeleteConfirmModal = ({
  isOpen,
  onClose,
  title = 'Confirm Delete',
  message,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  onConfirm,
  isDeleting = false,
}) => {
  const [modalRoot, setModalRoot] = useState(null);

  // Get modal root on mount
  useEffect(() => {
    setModalRoot(document.getElementById('modal-root') || document.body);
  }, []);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isDeleting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isDeleting, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isDeleting) {
      onClose();
    }
  };

  if (!isOpen || !modalRoot) return null;

  return ReactDOM.createPortal(
    <EnhancedThemeProvider>
      <Backdrop onClick={handleBackdropClick}>
        <ModalContainer
          role="alertdialog"
          aria-labelledby="delete-modal-title"
          aria-describedby="delete-modal-message"
          aria-modal="true"
          maxWidth="420px"
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton
            onClick={onClose}
            aria-label="Close modal"
            disabled={isDeleting}
          >
            <CloseIcon />
          </CloseButton>

          <ModalHeader>
            <ModalTitle id="delete-modal-title">{title}</ModalTitle>
          </ModalHeader>

          <ModalBody>
            <IconWrapper>
              <WarningIcon />
            </IconWrapper>
            <Message id="delete-modal-message">{message}</Message>
          </ModalBody>

          <CenteredFooter>
            <SecondaryButton
              type="button"
              onClick={onClose}
              disabled={isDeleting}
            >
              {cancelText}
            </SecondaryButton>
            <DeleteButton
              type="button"
              onClick={onConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : confirmText}
            </DeleteButton>
          </CenteredFooter>
        </ModalContainer>
      </Backdrop>
    </EnhancedThemeProvider>,
    modalRoot
  );
};

DeleteConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string.isRequired,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool,
};

export default DeleteConfirmModal;
